import { resolveForm, resolveField } from '/bb/identifier.js';
import { ensureCurrentPage, getOrCreateForm, getOrCreateField, markFieldChange } from '/bb/helpers.js';
import { getState } from '/bb/state.js';

let observer = null;
let mutationObserver = null;
let fallbackSyncHandler = null;
const observedForms = new Map();

function submitHandler(e) {
  const form = resolveForm(e.target);
  if (!form) return;
  syncVisibleForms();
  const pageKey = ensureCurrentPage();
  const f = getOrCreateForm(pageKey, form.id);
  f.is_submitted = true;
  const t = Date.now();
  f.submission_time_ms = f.runtime.firstVisibleAt > 0 ? Math.max(0, t - f.runtime.firstVisibleAt) : null;
  f.visits_before_submission_count = f.runtime.visitsSeen;
  const session = getState().session;
  session.pauseBeforeSubmitMs =
    session.lastFieldChangeAt > 0 ? Math.max(0, t - session.lastFieldChangeAt) : null;
}

function changeHandler(e) {
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  if (field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') {
    const form = getOrCreateForm(pageKey, field.formId);
    markFieldChange(form, field.fieldName);
    if (!fld.runtime.genericInputSeenSinceChange) {
      fld.changes_count = (fld.changes_count || 0) + 1;
    }
    fld.runtime.genericInputSeenSinceChange = false;
  }
}

function markFormVisible(form, t) {
  const pageKey = ensureCurrentPage();
  const f = getOrCreateForm(pageKey, form.id);
  if (f.runtime.firstVisibleAt === 0) f.runtime.firstVisibleAt = t;
}

function isVisible(element) {
  if (!element || !element.isConnected || typeof element.getBoundingClientRect !== 'function') return false;

  for (let current = element; current && current.nodeType === 1; current = current.parentElement) {
    const style = window.getComputedStyle(current);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      style.contentVisibility === 'hidden' ||
      Number(style.opacity) === 0
    ) {
      return false;
    }
  }

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  return rect.right > 0 && rect.bottom > 0 && rect.left < viewportWidth && rect.top < viewportHeight;
}

function onIntersect(entries) {
  if (document.visibilityState === 'hidden') return;

  const t = Date.now();
  for (const entry of entries) {
    const form = observedForms.get(entry.target);
    if (!form || !entry.isIntersecting || !isVisible(entry.target)) continue;
    markFormVisible(form, t);
  }
}

function rescanForms() {
  if (typeof document === 'undefined') return;

  const currentForms = new Set();
  const elements = document.querySelectorAll('form, [data-rs-form]');
  for (const element of elements) {
    const form = resolveForm(element);
    if (!form || form.element !== element) continue;
    currentForms.add(element);
    observedForms.set(element, form);
    if (observer) observer.observe(element);
  }

  for (const element of observedForms.keys()) {
    if (currentForms.has(element)) continue;
    observedForms.delete(element);
    if (observer) observer.unobserve(element);
  }
}

export function syncVisibleForms(t = Date.now()) {
  if (!attached || typeof document === 'undefined') return;
  if (document.visibilityState === 'hidden') return;

  rescanForms();
  for (const [element, form] of observedForms) {
    if (isVisible(element)) markFormVisible(form, t);
  }
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  if (typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(onIntersect, { threshold: 0 });
  } else if (typeof window !== 'undefined') {
    fallbackSyncHandler = () => syncVisibleForms();
    window.addEventListener('scroll', fallbackSyncHandler, { passive: true, capture: true });
    window.addEventListener('resize', fallbackSyncHandler, { passive: true });
  }
  if (typeof MutationObserver !== 'undefined') {
    mutationObserver = new MutationObserver(() => syncVisibleForms());
    mutationObserver.observe(document.body || document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-rs-form', 'id', 'name'],
    });
  }
  document.addEventListener('submit', submitHandler, { passive: true, capture: true });
  document.addEventListener('change', changeHandler, { passive: true, capture: true });
  attached = true;
  syncVisibleForms();
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }
  if (fallbackSyncHandler && typeof window !== 'undefined') {
    window.removeEventListener('scroll', fallbackSyncHandler, true);
    window.removeEventListener('resize', fallbackSyncHandler);
    fallbackSyncHandler = null;
  }
  observedForms.clear();
  document.removeEventListener('submit', submitHandler, true);
  document.removeEventListener('change', changeHandler, true);
  attached = false;
}
